import React, { useEffect, useState, useRef } from 'react';
import { connect } from "react-redux";
import { Redirect, Link } from "react-router-dom";
import * as Tools from "../../helpers/Tools";
import * as types from "../../constants/actionTypes";
import { logUI } from "../../api/andyliticsApi";
import Spinner from '../common/spinner';
import AppointmentOverview from './AppointmentOverview';
import GenericInput from '../common/input/GenericInput';
import Select from '../common/input/select';
import ZipCode from '../common/input/ZipInput';
import Email from '../common/input/Email';
import Text from '../common/input/text';
import PhoneNumber from '../common/input/PhoneNumber';

import InHomeSchedulingButtons from "./InHomeSchedulingButtons";
import SchedulingQuestions from './SchedulingQuestions';
import InfoModal from '../common/InfoModal';
import { inHomeWhereErrors } from '../../constants/textConstants';
import StickyProgressBar from '../common/StickyProgressBar';

const InHomeWhere = ({ selectedUsers, getVaccinePending, getVaccineResult,
    cancelBooking, getAvailableVaccines, prefilledDataPending, address1, address2, city, state,
    zipCode, email, mobilePhone, preferredLanguage, prefillLanguages, cities, extraNote, getInHomeServices, history }) => {

    useEffect(() => {
        logUI(`MBHWeb.UI.InHomeSchedulingWhere.Displayed`, new Date(), Tools.getURLPath());
    }, []);

    const [locationInfo, setLocationInfo] = useState({
        address1: address1 || "",
        address2: address2 || "",
        city: city.toLowerCase() || "",
        state: state || "TX",
        zipCode: zipCode || "",
        email: email || "",
        mobilePhone: mobilePhone || "",
        language: preferredLanguage || "",
        extraNote: extraNote
    })

    // the prefill may not have finished fetching by the time the user is on the screen, so let's watch for changes
    useEffect(() => {
        setLocationInfo({
            address1: address1 || "",
            address2: address2 || "",
            city: city.toLowerCase() || "",
            state: state || "TX",
            zipCode: zipCode || "",
            email: email || "",
            mobilePhone: mobilePhone || "",
            language: preferredLanguage || "",
            extraNote: extraNote
        })
    }, [address1, address2, city, state, zipCode, email, mobilePhone, preferredLanguage, prefillLanguages, extraNote])

    const [showModal, setShowModal] = useState(false);

    const initialRender = useRef(true);

    useEffect(() => {
        if (initialRender.current) {
            initialRender.current = false
        } else if (getVaccineResult && !getVaccineResult.success && getVaccineResult.errorMsg) {
            handlePopup();
        } else if (getVaccineResult && getVaccineResult.success) {
            history.push('/inhomeschedule/s4');
        }
    }, [getVaccineResult])

    const addressRef = useRef();
    const cityRef = useRef();
    const zipCodeRef = useRef();
    const emailRef = useRef();
    const mobilePhoneRef = useRef();
    const languageRef = useRef();


    const validationInfo = useRef({
        address1: false,
        city: false,
        zipCode: false,
        email: false,
        mobilePhone: false,
        language: false,
    })

    // created languages list so Select component can use array efficiently
    const languages = prefillLanguages?.reduce((acc, curr) => {
        let label = curr.value;
        let value = curr.key;
        acc.push({ label, value })
        return acc
    }, [])



    const handlePopup = () => {
        setShowModal(!showModal)
    }

    const handleInputChange = (event, inputField, isValid) => {
        let optionalFields = ['address2', 'extraNote', 'state']
        if (optionalFields.includes(inputField)) {
            setLocationInfo({ ...locationInfo, [inputField]: event.target.value })
        } else {
            setLocationInfo({ ...locationInfo, [inputField]: event.target.value })
        }
    }

    const childValidationMethod = (inputField, isValid) => {
        validationInfo.current[inputField] = isValid;
    }

    const runChildValidationMethods = () => {
        addressRef.current.childValidationMethod();
        cityRef.current.childValidationMethod();
        zipCodeRef.current.childValidationMethod();
        mobilePhoneRef.current.childValidationMethod()
        emailRef.current.childValidationMethod();
        languageRef.current.childValidationMethod();

    }

    const validateForm = () => {
        let isValid = validationInfo.current.address1 && validationInfo.current.city && validationInfo.current.zipCode && validationInfo.current.email && validationInfo.current.mobilePhone && validationInfo.current.language;
        return isValid;
    }

    const onClickPrevious = () => {
        logUI(`MBHWeb.UI.InHomeSchedulingWhere.Previous.Clicked`, new Date(), Tools.getURLPath());
        getInHomeServices(locationInfo, true);
        history.push("/inhomeschedule/s2")
    }

    const onClickNext = () => {
        runChildValidationMethods();

        if (validateForm()) {
            logUI(`MBHWeb.UI.InHomeSchedulingWhere.Next.Clicked`, new Date(), Tools.getURLPath());
            getInHomeServices(locationInfo);
            getAvailableVaccines();
        }
    }

    const closeModal = () => {
        setShowModal(false);
    }

    return (
        <>
            {!selectedUsers || selectedUsers?.length < 1 ? <Redirect to='/inhomeschedule/s1' /> :
                <>
                    <StickyProgressBar className="inhome" widthInPercent={1 / 3 * 100} />
                    <div className="container-fluid body-content inhome inhome-scheduling inhome-scheduling-container">
                        <div className="row">
                            <div className="col-12">
                                <div className="row flex">
                                    <div className="in-home-left-panel scheduling-wrapper">
                                        <div className="detail-wrapper where-detail-wrapper">
                                            <h1 className="page-heading">Where would you like to be vaccinated?</h1>
                                            <p className="subheading-info">Tell us where you’d like the health professional to meet you and some contact info in case they need to reach out to you.</p>
                                            {(prefilledDataPending || getVaccinePending) && <Spinner />}
                                            <div className="input-box">
                                                <h2 className="heading">Address</h2>
                                                <GenericInput
                                                    placeholder="Address 1"
                                                    value={locationInfo.address1}
                                                    onChange={(e, value, isValid) => handleInputChange(e, "address1", isValid)}
                                                    autoFocus={true}
                                                    required
                                                    ref={addressRef}
                                                    childValidationMethod={(isValid) => { childValidationMethod("address1", isValid) }}
                                                />
                                                <GenericInput
                                                    placeholder="Address 2 (Optional)"
                                                    value={locationInfo.address2}
                                                    onChange={(e) => handleInputChange(e, "address2", true)}
                                                />
                                                <Select
                                                    placeholder="City"
                                                    value={locationInfo.city}
                                                    onChange={(e, value, isValid) => handleInputChange(e, "city", isValid)}
                                                    options={cities}
                                                    required
                                                    childValidationMethod={(isValid) => { childValidationMethod("city", isValid) }}
                                                    ref={cityRef}
                                                />
                                                <div className="split-box">
                                                    <GenericInput placeholder="State" value={locationInfo.state} onChange={(e, value, isValid) => handleInputChange(e, "state", isValid)} disabled
                                                    />
                                                    <ZipCode
                                                        placeholder="Zip Code"
                                                        value={locationInfo.zipCode}
                                                        onChange={(e, isValid) => handleInputChange(e, "zipCode", isValid)}
                                                        required
                                                        childValidationMethod={(isValid) => { childValidationMethod("zipCode", isValid) }}
                                                        ref={zipCodeRef}
                                                    />
                                                </div>
                                            </div>
                                            <div className="input-box">
                                                <h2 className="heading">Contact Info</h2>
                                                <Email
                                                    placeholder="Email"
                                                    value={locationInfo.email}
                                                    onChange={(e, isValid) => handleInputChange(e, "email", isValid)}
                                                    required
                                                    childValidationMethod={(isValid) => { childValidationMethod("email", isValid) }}
                                                    ref={emailRef}
                                                />
                                                <div className="split-box">
                                                    <PhoneNumber
                                                        placeholder="Mobile Phone"
                                                        value={locationInfo.mobilePhone}
                                                        onChange={(e, isValid) => handleInputChange(e, "mobilePhone", isValid)}
                                                        required
                                                        childValidationMethod={(isValid) => { childValidationMethod("mobilePhone", isValid) }}
                                                        ref={mobilePhoneRef}
                                                    />
                                                    <Select
                                                        placeholder="Preferred Language"
                                                        value={locationInfo.language}
                                                        onChange={(e, value, isValid) => handleInputChange(e, "language", isValid)}
                                                        options={languages}
                                                        required
                                                        childValidationMethod={(isValid) => { childValidationMethod("language", isValid) }}
                                                        ref={languageRef}
                                                    />
                                                </div>
                                                <p>By providing BSWH your mobile number, you agree to receive text messages from BSWH and/or vendor partners
                                                    regarding the status of your vaccination appointment. Standard data and messaging rates may apply.
                                                </p>
                                            </div>
                                            <div className="input-box">
                                                <h2 className="heading">Notes to Provider</h2>
                                                <Text
                                                    placeholder="(Optional) e.g. Call first so we know to put up the dogs"
                                                    value={locationInfo.extraNote}
                                                    onChange={(e) => handleInputChange(e, "extraNote", true)}
                                                    rows="4"
                                                    maxSize={250}
                                                />
                                                <p className="limit-text">250 Character Limit</p>
                                            </div>
                                            <InHomeSchedulingButtons leftOnClick={onClickPrevious} rightOnClick={onClickNext} />
                                        </div>
                                        <SchedulingQuestions />
                                    </div>
                                    {
                                        showModal && <>
                                            <InfoModal
                                                alignText="center"
                                                hideBorder
                                                className="vaccine-model"
                                                isOpen={showModal}
                                                showModalHeader
                                                toggle={closeModal}
                                                modalBody={
                                                    <>
                                                        {getVaccineResult.errorMsg === inHomeWhereErrors.noVax ?
                                                            <div>
                                                                <span className="non-eligible-label-head">There are no vaccine appointments available at this time.</span><br />
                                                                <span className="non-eligible-msg">You can change your location
                                                                    or come back later and try again.</span>
                                                            </div> :
                                                            getVaccineResult.errorMsg === inHomeWhereErrors.notInServiceArea ?
                                                                <div>
                                                                    <span className="non-eligible-label-head">In-home vaccinations are not available in this area.</span><br />
                                                                    <span className="non-eligible-msg">You can try a different zip code or check back again later. We’ll be expanding our service area soon.</span>
                                                                </div> :
                                                                getVaccineResult.errorMsg === inHomeWhereErrors.notValidZip ?
                                                                    <div>
                                                                        <span className="non-eligible-label2">Incorrect ZIP Code</span>
                                                                        <span className="non-eligible-msg">The ZIP code you entered is not valid for {locationInfo.city.toUpperCase()}. Please try again.</span>
                                                                    </div>
                                                                    :
                                                                    <div>
                                                                        <span className="non-eligible-label2">Oops, something went wrong.</span>
                                                                        <span className="non-eligible-msg">{getVaccineResult.errorMsg || 'Please try again later if the issue persists.'}</span>
                                                                    </div>
                                                        }
                                                    </>
                                                }
                                                modalFooter={<>
                                                    {
                                                        getVaccineResult.errorMsg === inHomeWhereErrors.noVax || getVaccineResult.errorMsg === inHomeWhereErrors.notInServiceArea ?
                                                            <div>
                                                                <button className={`btn btn-primary btn-continue-e400`} onClick={closeModal}>UPDATE LOCATION</button><br />
                                                                <Link to="/" replace onClick={cancelBooking}>
                                                                    <span className={`btn`} onClick={closeModal}>CANCEL BOOKING</span>
                                                                </Link>
                                                            </div> :
                                                            <div>
                                                                <button className={`btn btn-primary btn-continue`} onClick={closeModal}>
                                                                    {getVaccineResult.errorMsg === inHomeWhereErrors.notValidZip ? 'TRY AGAIN' : 'OKAY'}
                                                                </button>
                                                            </div>
                                                    }

                                                </>}
                                            />

                                        </>

                                    }
                                    <div className="in-home-right-panel">
                                        <AppointmentOverview locationPhase={true} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            }</>
    );
}
const mapStateToProps = state => {
    const prefilledData = state.inHome.prefilledData;

    // if user has updated the form,  it will get saved as service location
    const serviceLocation = state.inHome.serviceLocationContactInfo

    return {
        selectedUsers: state.inHome.selectedUsers,
        prefilledDataPending: state.inHome.prefilledDataPending,
        profileList: state.memberships.profileList,
        address1: serviceLocation ? serviceLocation.address1 : prefilledData?.address1 || "",
        address2: serviceLocation ? serviceLocation.address2 : prefilledData?.address2 || "",
        city: serviceLocation ? serviceLocation.city : prefilledData?.city || "",
        state: serviceLocation ? serviceLocation.state : prefilledData?.state || "",
        zipCode: serviceLocation ? serviceLocation.zipCode : prefilledData?.zipCode || "",
        email: serviceLocation?.email ? serviceLocation.email : prefilledData?.email || "",
        mobilePhone: serviceLocation?.mobilePhone ? serviceLocation.mobilePhone : prefilledData?.mobilePhone || "",
        preferredLanguage: serviceLocation?.language ? serviceLocation.language : prefilledData?.preferredLanguage || "en",
        prefillLanguages: prefilledData?.languages,
        cities: prefilledData?.cities,
        extraNote: serviceLocation?.extraNote ? serviceLocation.extraNote : "",
        getVaccinePending: state.inHome.getVaccinePending,
        getVaccineResult: state.inHome.getVaccineResult

    }
}

const mapDispatchToProps = dispatch => {
    return {
        getInHomeServices: (address) => {
            dispatch({ type: types.SET_INHOME_LOCATION, address });
        },
        getAvailableVaccines: () => {
            dispatch({ type: types.GET_INHOME_VACCINES })
        },
        cancelBooking: () => {
            dispatch({ type: types.CANCEL_INHOME_BOOKING });
        }
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(InHomeWhere);